import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { BookHeart, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        toast.error("This account is not an admin.");
        return;
      }
      toast.success("Welcome back to your studio.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1 font-fredoka text-sm mb-6" style={{ color: "#6EC4E8" }} data-testid="admin-login-back">
          <ArrowLeft size={16} /> Back to the site
        </Link>
        <div className="warm-card p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: "#FF6B9D", color: "#fff" }}>
              <BookHeart size={24} />
            </span>
            <div>
              <p className="font-caveat text-xl" style={{ color: "#FF6B9D" }}>the writing studio</p>
              <h1 className="font-fredoka text-3xl leading-tight" style={{ color: "#2D3055" }}>Admin Login</h1>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl px-4 py-3 font-nunito focus:outline-none"
                style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl px-4 py-3 font-nunito focus:outline-none"
                style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                data-testid="admin-password-input"
              />
            </div>
            <button disabled={loading} className="btn-terracotta w-full justify-center" data-testid="admin-login-btn">
              {loading ? "Signing in…" : "Enter the studio"}
            </button>
          </form>

          <div className="mt-6 paper-card p-4 text-sm font-nunito" style={{ color: "#4A4E6D" }} data-testid="admin-login-hint">
            <p><span className="font-fredoka">Default credentials:</span></p>
            <p>Email: <code>admin@storybook.com</code></p>
            <p>Password: <code>admin123</code></p>
          </div>
        </div>
      </div>
    </div>
  );
}
